/**
 * Generic person data from frontend (vendor-agnostic)
 */
export interface PersonData {
  person_id: string;
  first_name: string;
  last_name: string;
  cpf?: string;
  identity_photo_url?: string;
  profile_picture_url?: string;
  isAllowed?: boolean;  // For access control
  rfid_tags?: string[];      // Hex RFID tag values (e.g. "25E31E") from vehicles
  vehicle_plates?: string[]; // License plates — used to build searchable name on ControlID
  validUntil?: string;  // ISO8601 — time-limited access (visitors); omit for permanent
  validFrom?: string;   // ISO8601 — access start time (visitors); omit for default
  isVisitor?: boolean;  // true = visitor/service_provider (affects userType on device)
}

/**
 * Device operations enum
 */
export enum DeviceOperation {
  SYNC_PERSON = 'sync_person',
  REMOVE_PERSON = 'remove_person',
  CHECK_HEALTH = 'check_health',
  UPLOAD_FACE = 'upload_face',
  LOAD_PEOPLE = 'load_people',
  OPEN_DOOR = 'open_door',
  ENROLL_FINGERPRINT = 'enroll_fingerprint',
  FETCH_FINGERPRINTS = 'fetch_fingerprints',
  PUSH_FINGERPRINT = 'push_fingerprint',
  DELETE_FINGERPRINT = 'delete_fingerprint',
}

/**
 * Device information interface (minimal for adapters)
 * Compatible with DeviceInfo from deviceCommunication.ts
 */
export interface DeviceInfo {
  id: string;
  name?: string;
  vendor: string;
  ip_address?: string;
  local_ip_address?: string;
  port?: string;
  username: string;
  password: string;
  /** Zone entry type — 'entrada' = entry only, 'saida' = exit only, 'passagem' = both */
  direction?: 'entrada' | 'saida' | 'passagem';
}

/**
 * Base interface for device vendor adapters
 * Each vendor adapter implements this interface
 */
export interface IDeviceAdapter {
  vendor: 'hikvision' | 'controlid' | 'rfid';

  /**
   * Format person data for sync operation
   * @param personData - Generic person data from frontend
   * @returns Vendor-specific payload (can be async for photo download)
   */
  formatSyncPersonPayload(personData: PersonData): any | Promise<any>;

  /**
   * Format person data for removal operation
   */
  formatRemovePersonPayload(devicePersonId: string): any;

  /**
   * Format face photo upload payload
   * @param employeeNo - Device-specific employee number or ID (from sync response)
   *                     Can be string (MD5 hash) or number (device user ID)
   * @param photoBase64 - Base64 encoded photo data
   * @returns Vendor-specific face upload payload
   */
  formatUploadFacePayload(employeeNo: string | number, photoBase64: string): any;

  /**
   * Generate device-specific employee/user ID from person data
   * @param personData - Generic person data
   * @returns Device-specific identifier (e.g., MD5 hash for Hikvision)
   */
  generateDevicePersonId(personData: PersonData): string;

  /**
   * Get endpoint for operation
   */
  getEndpoint(operation: DeviceOperation, device: DeviceInfo): string;

  /**
   * Get HTTP method for operation
   */
  getMethod(operation: DeviceOperation): 'GET' | 'POST' | 'PUT' | 'DELETE';

  /**
   * Format load people payload for fetching all users from device
   * @returns Vendor-specific payload for loading users
   */
  formatLoadPeoplePayload(payload?: any): any;

  /**
   * Format payload for door open (XML for Hikvision ISAPI)
   * Returns { _xml: true, content: '<RemoteControlDoor ...>' } for Hikvision
   * Optional — only vendors that support direct door open implement this
   */
  formatOpenDoorPayload?(): any;

  /**
   * Parse load people response to common format
   * @param responseData - Raw response from device
   * @returns Array of people in common format
   */
  parseLoadPeopleResponse(responseData: any): Array<{
    employeeNo: string;
    name: string;
    userType: string;
  }>;

  // ==================== SESSION AUTHENTICATION METHODS ====================

  /**
   * Check if device requires session-based authentication
   * @param device - Device information
   * @returns Promise<boolean> - true if session auth required, false otherwise
   */
  requiresSessionAuth(device: DeviceInfo): Promise<boolean>;

  /**
   * Detect device firmware version to determine authentication method
   * Optional method - not all vendors need firmware detection
   * @param device - Device information
   * @returns Promise<FirmwareInfo | null> - Firmware info or null if not applicable
   */
  detectFirmwareVersion?(device: DeviceInfo): Promise<any>;

  /**
   * Establish session with device
   * @param request - Session establishment request parameters
   * @returns Promise<SessionEstablishmentResult> - Session result with token and metadata
   */
  establishSession(request: any): Promise<any>;

  /**
   * Format URL with session parameters
   * @param endpoint - Base endpoint URL
   * @param session - Session information
   * @returns string - URL with session query parameters appended
   */
  formatUrlWithSession(endpoint: string, session: any): string;

  /**
   * Format headers with session information
   * @param baseHeaders - Base HTTP headers
   * @param session - Session information
   * @returns Record<string, string> - Headers with session-specific headers added
   */
  formatHeadersWithSession(
    baseHeaders: Record<string, string>,
    session: any
  ): Record<string, string>;

  /**
   * Check if session is still valid
   * @param session - Session to validate
   * @returns boolean - true if session is valid and not expired
   */
  isSessionValid(session: any): boolean;
}
